import React from 'react';
import { AINavigator } from '../components/AINavigator';

export const AINavigatorPage: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="h-12 w-12 bg-consulting-slate-blue-100 flex items-center justify-center">
            <span className="text-consulting-slate-blue-600 font-bold text-xl">AI</span>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">
              Knowledge Navigator
            </h1>
            <p className="text-xl text-charcoal-600">
              AI-Powered Intelligent Guide
            </p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-charcoal-700 leading-relaxed">
            The Knowledge Navigator is an AI-powered intelligent guide that helps you discover, 
            understand, and apply the Peak Priorities knowledge system. It provides personalized 
            recommendations, contextual insights, and guided learning paths.
          </p>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center">
        <span className="px-4 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm font-semibold rounded-full">
          🧪 Development Feature
        </span>
      </div>

      {/* AI Navigator Component */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Interactive AI Navigator</h2>
        <div className="min-h-[400px]">
          <AINavigator />
        </div>
      </div>

      {/* Features Overview */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Key Features</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Intelligent Guidance</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">•</span>
                <span className="text-charcoal-700">Personalized learning recommendations</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">•</span>
                <span className="text-charcoal-700">Context-aware content suggestions</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">•</span>
                <span className="text-charcoal-700">Adaptive learning paths</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">•</span>
                <span className="text-charcoal-700">Real-time assistance and explanations</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Advanced Capabilities</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-charcoal-700">Natural language understanding</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-charcoal-700">Knowledge graph navigation</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-charcoal-700">Multi-modal interaction support</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-charcoal-700">Continuous learning and improvement</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Development Notes */}
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-6">
        <h3 className="text-lg font-semibold text-[var(--mustard)] mb-3">Development Status</h3>
        <p className="text-[var(--mustard)] text-sm leading-relaxed">
          The Knowledge Navigator is currently in active development as part of The Lab. 
          This feature represents the cutting edge of AI-powered knowledge assistance, 
          combining natural language processing, knowledge graphs, and personalized learning algorithms 
          to create an intelligent guide for the Peak Priorities system.
        </p>
      </div>
    </div>
  );
};
